import { makeStyles, Theme } from '@material-ui/core';
import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { PanoramaDetails } from '../services/UploadManagementServiceTypes';
import { PanoramaThumbnail } from './PanoramaThumbnail';

export interface PanoramaThumbnailDnDProps {
  currentPanorama?: PanoramaDetails;
  panoramas: PanoramaDetails[];
  onPanoramaClick: (panorama: PanoramaDetails) => void;
  onPanoramaDeleteClick: (panorama: PanoramaDetails) => void;
  onPanoramaMoved: (args: { panoramaId: string; oldPosition: number; newPosition: number }) => void;
  contentFits: boolean;
  thumbnailContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  displayActions: boolean;
}

export const PanoramaThumbnailDnD: React.FC<PanoramaThumbnailDnDProps> = ({
  currentPanorama,
  onPanoramaMoved,
  panoramas,
  onPanoramaClick,
  thumbnailContainerRef,
  onPanoramaDeleteClick,
  contentFits,
  displayActions,
}) => {
  const styles = useThumbnailDndStyle({ contentFits });
  return (
    <DragDropContext
      onDragEnd={(m) => {
        if (m.destination) {
          onPanoramaMoved({
            panoramaId: m.draggableId,
            oldPosition: m.source.index,
            newPosition: m.destination.index,
          });
        }
      }}
    >
      <Droppable
        renderClone={(provided, snapshot, rubric) => {
          return (
            <PanoramaThumbnail
              {...provided}
              {...snapshot}
              displayActions={true}
              isCurrent={currentPanorama === panoramas[rubric.source.index]}
              panorama={panoramas[rubric.source.index]}
              onPanoramaDeleteClick={onPanoramaDeleteClick}
              onPanoramaClick={onPanoramaClick}
            />
          );
        }}
        droppableId='droppableThumbnails'
        direction='horizontal'
      >
        {(provided) => (
          <div
            role='tablist'
            className={styles.thumbnailContainerList}
            ref={(ref) => {
              provided.innerRef(ref);
              thumbnailContainerRef.current = ref;
            }}
            {...provided.droppableProps}
          >
            {panoramas.map((panorama, index) => (
              <Draggable
                key={`${panorama.panoramaGuid}-${index}`}
                draggableId={`${panorama.panoramaGuid}-${index}`}
                index={index}
              >
                {(provided1, snapshot) => (
                  <PanoramaThumbnail
                    {...provided1}
                    {...snapshot}
                    displayActions={displayActions}
                    isCurrent={currentPanorama === panorama}
                    panorama={panorama}
                    onPanoramaDeleteClick={onPanoramaDeleteClick}
                    onPanoramaClick={onPanoramaClick}
                  />
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const useThumbnailDndStyle = makeStyles<Theme, { contentFits: boolean }>(() => ({
  thumbnailContainerList: {
    '-ms-overflow-style': 'none' /* IE and Edge */,
    'scrollbar-width': 'none' /* Firefox */,
    '&::-webkit-scrollbar': {
      display: 'none', //chrome
    },
    overflowX: 'auto',
    overflowY: 'hidden',
    height: '100%',
    display: 'flex',
    margin: '0 8px',
    justifyContent: ({ contentFits }) => (contentFits ? 'center' : 'initial'),
  },
}));
